import { Link } from '@remix-run/react'
import {
  BlocksRenderer,
  type BlocksContent,
} from '@strapi/blocks-react-renderer'
import { useTranslation } from 'react-i18next'

import { defaultLocale } from '#app/utils/i18n.ts'
import { cn } from '#app/utils/misc.tsx'

export function RichText({
  content,
  justifyText = false,
}: {
  content: BlocksContent
  justifyText?: boolean
}) {
  const { i18n } = useTranslation()

  return (
    <BlocksRenderer
      content={content}
      blocks={{
        paragraph: ({ children }) => (
          <p
            className={cn(
              'mb-6 text-body-sm font-medium lg:text-body-sm',
              justifyText && 'text-last-center text-justify',
            )}
          >
            {children}
          </p>
        ),
        heading: ({ children, level }) => {
          switch (level) {
            case 1:
              return <h1 className="mb-4 text-h1">{children}</h1>
            case 2:
              return <h2 className="mb-4 text-h2">{children}</h2>
            case 3:
              return <h3 className="mb-4 text-h3">{children}</h3>
            case 4:
              return <h4 className="mb-4 text-h4">{children}</h4>
            case 5:
              return <h5 className="mb-4 text-h5">{children}</h5>
            case 6:
              return <h6 className="mb-4 text-h6">{children}</h6>
            default:
              return null
          }
        },
        link: ({ children, url }) => (
          <Link
            to={`${i18n.language === defaultLocale ? '' : '/en'}${url}`}
            target={url.includes('https:') ? '_blank' : undefined}
            rel={url.includes('https:') ? 'noopener noreferrer' : undefined}
            className="font-medium text-primary underline"
          >
            {children}
          </Link>
        ),
        list: ({ children }) => (
          <ul className="mb-6 list-disc pl-6 text-body-sm leading-8">
            {children}
          </ul>
        ),
      }}
      modifiers={{
        bold: ({ children }) => <b>{children}</b>,
        italic: ({ children }) => <span className="italic">{children}</span>,
      }}
    />
  )
}
